import { centsToDollars, formatDollars } from '@fruitful/fruitils';
import {
    AnalyticsUserEvent,
    ApexDerivedSubState,
    type ImAccount,
    ReadableApexAccountRegistrationType,
} from '@fruitful/types';
import { router } from 'expo-router';
import { useSetAtom } from 'jotai';
import { useMemo, useState } from 'react';
import {
    BadgeLabel,
    type BadgeLabelVariant,
    BodyLargeEmphasized,
    BodyLargeRegular,
    BodyMediumRegular,
    BodySmallRegular,
    Clock,
    FootnoteRegular,
    FRUITFUL_ICON_KEY,
    FruitfulCustomIcon,
    H1Emphasized,
    type IconType,
    LastUpdated,
    MessageCircleX,
    NavigationRow,
    Notice,
    Row,
    RowList,
    SmoothieScrollView,
    Spinner,
    TriangleAlert,
    View,
    XStack,
    YStack,
} from 'smoothie';

import TabHeader from '../../common/components/TabHeader';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import { useTrackViewOnFocus } from '../../events/hooks/useTrackViewOnFocus';
import { useShowGuideMessaging } from '../../guideMessaging/hooks/useShowGuideMessaging';
import { track } from '../../logger/Logger';
import { useGetPartnerSoloApexAccounts } from '../hooks/useGetApexAccounts';
import { useGetApexPortfolioPerformance } from '../hooks/useGetApexPerformance';
import { closedImAccountsAtom } from '../state/investmentAtoms';
import {
    type ApexPerformanceDatapoint,
    ApexPerformanceHorizon,
} from '../types/performance';
import {
    ApexAccountHorizonSelectionRow,
    ApexAccountPerformanceGraph,
    ApexSnapshotRow,
} from './performance/ApexPerformance';

const formatSignedDollars = (amount: number): string => {
    const formatted = formatDollars(amount);
    return amount >= 0 ? `+${formatted}` : formatted;
};

const formatSignedPercent = (percent: number): string => {
    const formatted = `${percent.toFixed(0)}%`;
    return percent >= 0 ? `+${formatted}` : formatted;
};

export const NonOpenAccountRow = ({
    account,
    text,
    variant,
    icon,
    onPress,
    inactive = false,
}: {
    account: ImAccount;
    text: string;
    variant: BadgeLabelVariant;
    icon: IconType;
    onPress?: () => void;
    inactive?: boolean;
}) => {
    return (
        <AccountRow
            account={account}
            inactive={inactive}
            onPressOverride={onPress}
            rightContentOverride={
                <BadgeLabel variant={variant} text={text} icon={icon} />
            }
        />
    );
};

export const AccountRow = ({
    account,
    onPressOverride,
    rightContentOverride,
    inactive = false,
}: {
    account: ImAccount;
    onPressOverride?: () => void;
    rightContentOverride?: React.ReactNode;
    inactive?: boolean;
}) => {
    return (
        <Row
            onPress={
                inactive
                    ? undefined
                    : (onPressOverride ??
                      (() => {
                          router.navigate({
                              pathname: '/investments/account-overview',
                              params: {
                                  apexAccountId: account.apexAccountId,
                              },
                          });
                      }))
            }
            title={account.nickname}
            subtitle={
                ReadableApexAccountRegistrationType[account.registrationType]
            }
            leftContentOverride={
                <FruitfulCustomIcon
                    icon={account.icon ?? FRUITFUL_ICON_KEY}
                    color={account.color ?? 'peach'}
                    useAltColors={true}
                    size="sm"
                    muted={inactive}
                />
            }
            rightContent={
                rightContentOverride ?? (
                    <BodyMediumRegular>
                        {formatDollars(
                            centsToDollars(
                                account.realtimeBalanceData.accountEquityCents,
                            ),
                        )}
                    </BodyMediumRegular>
                )
            }
            containerProps={{
                backgroundColor: '$gray100',
                ...(inactive && { opacity: 0.6 }),
            }}
        />
    );
};

export const getAccountDisplayComponent = (
    account: ImAccount,
    { inactive = false }: { inactive?: boolean } = {},
) => {
    if (
        account.derivedSubState === ApexDerivedSubState.CLOSED ||
        account.derivedSubState === ApexDerivedSubState.OPEN_IN_CLOSING
    ) {
        return (
            <NonOpenAccountRow
                key={account.apexAccountId}
                account={account}
                text="Closed"
                variant="warning"
                icon={MessageCircleX}
                inactive={inactive}
                onPress={
                    inactive
                        ? undefined
                        : () => {
                              router.navigate({
                                  pathname: '/investments/account-overview',
                                  params: {
                                      apexAccountId: account.apexAccountId,
                                  },
                              });
                          }
                }
            />
        );
    }

    if (account.derivedSubState === ApexDerivedSubState.OPEN_RESTRICTED) {
        return (
            <NonOpenAccountRow
                account={account}
                key={account.apexAccountId}
                text="Restricted"
                variant="danger"
                icon={TriangleAlert}
                inactive={inactive}
                onPress={
                    inactive
                        ? undefined
                        : () => {
                              router.navigate({
                                  pathname: '/investments/account-overview',
                                  params: {
                                      apexAccountId: account.apexAccountId,
                                  },
                              });
                          }
                }
            />
        );
    }

    if (
        account.derivedSubState ===
            ApexDerivedSubState.PENDING_PARTNER_ACCEPT ||
        account.derivedSubState ===
            ApexDerivedSubState.PENDING_INVESTIGATION_MANUAL_REVIEW ||
        account.derivedSubState ===
            ApexDerivedSubState.PENDING_INVESTIGATION_RUN
    ) {
        return (
            <NonOpenAccountRow
                account={account}
                key={account.apexAccountId}
                text="Pending"
                variant="warning"
                icon={Clock}
                inactive={inactive}
                onPress={inactive ? undefined : () => {}}
            />
        );
    }

    return (
        <AccountRow
            account={account}
            key={account.apexAccountId}
            inactive={inactive}
        />
    );
};

export const InvestmentsOverview = ({
    imAccounts,
    dataUpdatedAt,
}: {
    imAccounts: ImAccount[];
    dataUpdatedAt: number;
}) => {
    const user = useAuthedUser();
    const showGuideMessaging = useShowGuideMessaging();
    const { data: partnerImAccounts, isLoading: isPartnerAccountsLoading } =
        useGetPartnerSoloApexAccounts(user.partnerMember != null);

    const [performanceSnapshot, setPerformanceSnapshot] =
        useState<ApexPerformanceDatapoint | null>(null);

    const [selectedPerformanceHorizon, setSelectedPerformanceHorizon] =
        useState<ApexPerformanceHorizon>(ApexPerformanceHorizon.ALL);

    const [isChartScrollLocked, setIsChartScrollLocked] = useState(false);

    const { data: apexPerformanceData, isLoading: isPerformanceLoading } =
        useGetApexPortfolioPerformance();

    const setClosedImAccounts = useSetAtom(closedImAccountsAtom);

    const selectedPerformanceSeries = useMemo(() => {
        return apexPerformanceData?.[selectedPerformanceHorizon] ?? null;
    }, [apexPerformanceData, selectedPerformanceHorizon]);

    const performanceSeriesWithData =
        selectedPerformanceSeries != null &&
        selectedPerformanceSeries.datapoints.length > 0
            ? selectedPerformanceSeries
            : null;

    const totalBalance = useMemo(() => {
        const accounts = [...imAccounts, ...(partnerImAccounts ?? [])];
        return accounts.reduce((acc, cur) => {
            return (
                acc + centsToDollars(cur.realtimeBalanceData.accountEquityCents)
            );
        }, 0);
    }, [imAccounts, partnerImAccounts]);

    const closedAccounts = useMemo(() => {
        return imAccounts.filter(
            (c) =>
                c.derivedSubState === ApexDerivedSubState.CLOSED ||
                c.derivedSubState === ApexDerivedSubState.OPEN_IN_CLOSING,
        );
    }, [imAccounts]);
    setClosedImAccounts(closedAccounts);

    const sortedAccounts = useMemo(() => {
        return imAccounts
            .filter(
                (a) =>
                    a.derivedSubState !== ApexDerivedSubState.CLOSED &&
                    a.derivedSubState !== ApexDerivedSubState.OPEN_IN_CLOSING,
            )
            .sort((a, b) => {
                return (
                    new Date(a.createdAt).getTime() -
                    new Date(b.createdAt).getTime()
                );
            });
    }, [imAccounts]);

    const accountsToShow = useMemo(() => {
        return sortedAccounts.map((account) => {
            return getAccountDisplayComponent(account);
        });
    }, [sortedAccounts]);

    const partnerAccountsToShow = useMemo(() => {
        return (partnerImAccounts ?? [])
            .filter(
                (account) =>
                    account.derivedSubState !== ApexDerivedSubState.CLOSED &&
                    account.derivedSubState !==
                        ApexDerivedSubState.OPEN_IN_CLOSING,
            )
            .sort((a, b) => {
                return (
                    new Date(a.createdAt).getTime() -
                    new Date(b.createdAt).getTime()
                );
            })
            .map((account) => {
                return getAccountDisplayComponent(account, { inactive: true });
            });
    }, [partnerImAccounts]);

    useTrackViewOnFocus('Investment/Overview');

    return (
        <View height="100%">
            <TabHeader headerTitle="Investments" />
            <SmoothieScrollView
                containerStyle={{ marginHorizontal: 24 }}
                scrollEnabled={!isChartScrollLocked}
            >
                <YStack gap={16} $sm={{ width: 640 }} $md={{ width: 700 }}>
                    <YStack backgroundColor="$gray100" borderRadius={24}>
                        <YStack gap={4} padding={24} height={120}>
                            {performanceSnapshot == null ? (
                                <>
                                    <BodySmallRegular color="$gray1000">
                                        Total balance
                                    </BodySmallRegular>
                                    <H1Emphasized>
                                        {formatDollars(totalBalance)}
                                    </H1Emphasized>
                                </>
                            ) : (
                                <ApexSnapshotRow
                                    performanceSnapshot={performanceSnapshot}
                                />
                            )}
                        </YStack>

                        {isPerformanceLoading && (
                            <YStack paddingVertical={48}>
                                <Spinner />
                            </YStack>
                        )}
                        {performanceSeriesWithData != null && (
                            <>
                                <YStack padding={24} paddingTop={0} gap={4}>
                                    <BodySmallRegular color="$gray1000">
                                        Performance
                                    </BodySmallRegular>
                                    <XStack
                                        alignItems="baseline"
                                        gap={6}
                                        flexWrap="wrap"
                                    >
                                        <BodyLargeEmphasized>
                                            {formatSignedDollars(
                                                performanceSeriesWithData.amountReturn,
                                            )}
                                        </BodyLargeEmphasized>
                                        {performanceSeriesWithData.percentageReturn !=
                                            null && (
                                            <BodyLargeRegular color="$gray1000">
                                                {`(${formatSignedPercent(
                                                    performanceSeriesWithData.percentageReturn,
                                                )})`}
                                            </BodyLargeRegular>
                                        )}
                                    </XStack>
                                </YStack>

                                <YStack paddingVertical={16}>
                                    <ApexAccountPerformanceGraph
                                        backgroundColor="$gray100"
                                        datapoints={
                                            performanceSeriesWithData.datapoints
                                        }
                                        onActiveDatapointChange={
                                            setPerformanceSnapshot
                                        }
                                        onScrollLock={setIsChartScrollLocked}
                                    />

                                    <ApexAccountHorizonSelectionRow
                                        selectedPerformanceHorizon={
                                            selectedPerformanceHorizon
                                        }
                                        onSelect={setSelectedPerformanceHorizon}
                                    />
                                </YStack>
                            </>
                        )}
                    </YStack>

                    <RowList
                        rowHeaderProps={{ title: 'Your investment accounts' }}
                    >
                        {accountsToShow}
                    </RowList>
                    {(isPartnerAccountsLoading ||
                        partnerAccountsToShow.length > 0) && (
                        <RowList
                            rowHeaderProps={{
                                title: "Your partner's investment accounts",
                            }}
                        >
                            {isPartnerAccountsLoading ? (
                                <YStack
                                    paddingVertical={24}
                                    alignItems="center"
                                >
                                    <Spinner />
                                </YStack>
                            ) : (
                                partnerAccountsToShow
                            )}
                        </RowList>
                    )}
                    <YStack>
                        <Notice
                            variant="neutral"
                            title="Want to open another account?"
                            description="Message your Guide to open an investment account."
                            action={{
                                text: 'Message my Guide',
                                onPress: () => {
                                    track(
                                        AnalyticsUserEvent.IM_OPEN_ANOTHER_ACCOUNT,
                                    );
                                    showGuideMessaging();
                                },
                            }}
                        />
                    </YStack>
                    {closedAccounts.length > 0 && (
                        <YStack>
                            <NavigationRow
                                title="Closed accounts"
                                containerProps={{
                                    background: '$gray200',
                                    borderRadius: 24,
                                }}
                                onPress={() => {
                                    router.navigate(
                                        '/investments/closed-accounts',
                                    );
                                }}
                            />
                        </YStack>
                    )}

                    <FootnoteRegular alignSelf="center" color="$gray900">
                        Investments are held at and cleared by APEX Clearing
                        Corporation, member FINRA/SIPC.
                    </FootnoteRegular>

                    <LastUpdated dataUpdatedAt={dataUpdatedAt} />
                </YStack>
            </SmoothieScrollView>
        </View>
    );
};
